import React from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Users, Award, Clock, BookOpen, Briefcase, Check, ArrowRight, Heart, Scissors, Sparkles, DollarSign, MessageCircle, HeadphonesIcon, ClipboardCheck, Star, Phone, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { courseDetails } from '../mock';
import SEO from '../components/SEO';

const AcademyPage = () => {
    // List the 4 images you want to rotate through:
  const images = [
    "emilymegan5.jpeg",
    "emilymegan3.jpeg",
    "emilymegan2.jpeg",
    "emilymegan4.jpeg"
  ];

  const [currentIndex, setCurrentIndex] = React.useState(0);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 3000); // 3000ms = 3 seconds
    return () => clearInterval(timer);
  }, []);
  return (
    <div className="min-h-screen bg-cream-50">
      <SEO 
        title="Dog Grooming Academy Manchester | CPD Accredited Course"
        description="CPD accredited dog grooming course in Manchester. 5-day intensive hands-on training with professional Artero starter kit worth £800. Small class sizes, expert tutors in Denton."
        keywords="dog grooming course Manchester, dog grooming academy Manchester, learn dog grooming Manchester, CPD accredited grooming course Manchester, become a dog groomer Manchester, dog grooming training Manchester, dog grooming school Manchester"
      />
      {/* Hero Section */}
      <section className="bg-white !pt-[80px] !pb-[0px]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-sage-100 text-sage-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              {courseDetails.accreditation}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-sage-900 mb-6">
              Learn to Be a Dog Groomer
            </h1>
            <p className="text-xl text-sage-800 leading-relaxed" style={{marginBottom: '20px'}}>
              Turn your passion for dogs into a rewarding career with our intensive 5-day 
              CPD Accredited grooming course
            </p>
          </div>
        </div>
      </section>

      {/* Course Overview */}
      <section className="!pt-[80px] !pb-[40px] bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
           <img
    src={images[currentIndex]}
    alt="Pups & Dreams Academy graduates rotating display"
    className="rounded-3xl shadow-2xl w-full h-auto transition-opacity duration-500" />
            </div>
            <div className="space-y-6 lg:pt-4">
              <h2 className="text-3xl md:text-4xl font-bold text-sage-900">
                {courseDetails.title}
              </h2>
              <p className="text-lg text-sage-700 leading-relaxed">
                A course designed by Dog Groomers, to give you a taster of where to start. 
                Learn from real groomers at Pups & Dreams Spa & Academy and turn your passion into a career!
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-sage-50 rounded-lg">
                  <Clock className="text-sage-600 flex-shrink-0" size={24} />
                  <div>
                    <div className="font-semibold text-sage-900">{courseDetails.schedule}</div>
                    <div className="text-sm text-sage-600">{courseDetails.duration}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-sage-50 rounded-lg">
                  <Users className="text-sage-600 flex-shrink-0" size={24} />
                  <div>
                    <div className="font-semibold text-sage-900">{courseDetails.classSize}</div>
                    <div className="text-sm text-sage-600">Individual attention</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-sage-50 rounded-lg">
                  <Award className="text-sage-600 flex-shrink-0" size={24} />
                  <div>
                    <div className="font-semibold text-sage-900">CPD Accredited</div>
                    <div className="text-sm text-sage-600">Recognised certification</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-sage-50 rounded-lg">
                  <GraduationCap className="text-sage-600 flex-shrink-0" size={24} />
                  <div>
                    <div className="font-semibold text-sage-900">{courseDetails.requirements}</div>
                    <div className="text-sm text-sage-600">Just bring passion!</div>
                  </div>
                </div>
              </div>

              <p className="text-sm text-sage-600 italic">
                Our graduates on graduation day — certificates, flowers and all smiles!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Our Course is Different */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto space-y-6 text-sage-800 text-lg leading-relaxed">
            <p>
              In the UK, the dog grooming industry remains <strong>unregulated</strong>, meaning there's{' '}
              <strong>no mandatory qualification or licence</strong> needed to start a dog grooming career.
              Government regulated courses (<strong>Ofqual</strong>) are available, but they're{' '}
              <strong>not</strong> a legal requirement to work as a dog groomer.
            </p>
            <p>
              At Pups &amp; Dreams, our 5-day course is <strong>CPD-accredited</strong> and recognised for
              delivering quality learning. But what truly makes our training stand out is how{' '}
              <strong>real</strong> and <strong>hands-on</strong> it is. Unlike some Ofqual-regulated courses,
              which can be classroom-heavy and theory-led, our course is built around{' '}
              <strong>practical, real-world experience</strong>. You'll be grooming real dogs, in a working
              salon, learning directly from experienced professionals who do the job everyday. We focus on
              teaching you what we feel matters in the dog grooming world;{' '}
              <strong>practical skills, confidence, speed and styling</strong> (at a fraction of the cost too!)
              Megan completed a CPD dog grooming diploma and Emily completed the Ofqual IPET level 3 Diploma,
              yet both of us are at the exact same point in our career and equally as successful.
            </p>
            <p>
              We can't wait to help you start your journey!
            </p>
          </div>
        </div>
      </section>

      {/* Why Dog Grooming Matters */}
      <section className="py-20 bg-gradient-to-br from-sage-50 to-sage-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-6">
              More Than Just a Career
            </h2>
            <p className="text-xl text-sage-700 leading-relaxed mb-8">
              Becoming a dog groomer is more than a career — it's a commitment to the wellbeing, 
              comfort, and confidence of every dog that walks through the door.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
            {
              title: 'Supporting Canine Health',
              description: 'Regular coat care promotes healthy skin, early detection of issues, and overall wellbeing'
            },
            {
              title: 'Comfort & Calmness',
              description: 'Helping dogs feel clean, calm, and cared for in every grooming session'
            },
            {
              title: 'Building Trust',
              description: 'Working with dogs of all breeds and temperaments, creating positive experiences'
            },
            {
              title: 'Safe Environment',
              description: 'Creating calm, safe spaces where dogs feel secure and confident'
            },
            {
              title: 'Lasting Relationships',
              description: 'Forming meaningful connections with pets and their owners'
            },
            {
              title: 'Care & Compassion',
              description: 'Grooming is about more than appearance — it\'s about connection and compassion'
            }].
            map((item, index) =>
            <Card key={index} className="border-sage-200 bg-white hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-sage-900 mb-2">{item.title}</h3>
                  <p className="text-sage-600">{item.description}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Is This Course For You? */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-4">
                Is This Course For You?
              </h2>
              <p className="text-lg text-sage-600">
                Our training is perfect if you answer YES to any of these...
              </p>
            </div>

            <div className="space-y-4">
              {[
                'Do you dream of working hands-on with dogs every day?',
                'Want a career that\'s creative, rewarding, and full of wagging tails?',
                'Looking to build up a side hustle before going head first into it?',
                'Ready to turn your passion for dogs into a professional skill?'
              ].map((question, index) => (
                <div key={index} className="flex flex-col items-center justify-center text-center gap-4 p-6 bg-gradient-to-r from-sage-50 to-sage-100 rounded-xl hover:shadow-md transition-shadow">
                  <div className="w-8 h-8 bg-gradient-to-br from-sage-500 to-sage-700 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="text-white" size={18} />
                  </div>
                  <p className="text-lg text-sage-900 font-medium">{question}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 p-8 bg-sage-900 rounded-2xl text-white text-center">
              <p className="text-xl font-semibold mb-2">
                At Pups & Dreams, we don't just teach dog grooming — we live it.
              </p>
              <p className="text-sage-100">
                Our courses are designed and taught by professional dog groomers, giving you 
                real-world skills, confidence, and guidance to succeed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-12 bg-gradient-to-br from-sage-50 to-sage-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-3">
              What You'll Learn
            </h2>
            <p className="text-lg text-sage-600 max-w-2xl mx-auto">
              Comprehensive training covering everything you need to start your grooming business
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-6xl mx-auto">
            {courseDetails.curriculum.map((topic, index) => {
              const icons = [Heart, Scissors, Sparkles, GraduationCap, BookOpen, Scissors, Briefcase, ClipboardCheck, MessageCircle, Award, Phone, DollarSign, Check];
              const IconComponent = icons[index % icons.length];
              return (
                <Card key={index} className="border-sage-200 bg-white hover:shadow-lg transition-all hover:-translate-y-1">
                  <CardContent className="p-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-sage-500 to-sage-700 rounded-lg flex items-center justify-center mb-2">
                      <IconComponent className="text-white" size={20} />
                    </div>
                    <h3 className="text-sm font-bold text-sage-900 leading-tight">{topic}</h3>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="mt-10 max-w-2xl mx-auto text-center">
            <p className="text-lg text-sage-700 leading-relaxed">
              By the end of this introductory course, you'll walk away with real hands-on experience,
              as well as a <strong className="text-sage-900">CPD accredited certificate of completion</strong> to
              kickstart your dog grooming journey...
            </p>
          </div>
        </div>
      </section>

      {/* Training Video */}
      <section style={{paddingTop: '80px', paddingBottom: '48px'}} className="bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-3">
                See Our Training in Action
              </h2>
              <p className="text-lg text-sage-600">
                Watch our students learning hands-on grooming techniques in our professional salon
              </p>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-sage-200">
              <video 
                controls 
                className="w-full h-auto"
              >
                <source src="https://customer-assets.emergentagent.com/job_pups-dreams-spa/artifacts/mypbarfa_VIDEO-2026-03-03-21-15-45.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            
            {/* Training Photo Gallery */}
            <div className="mt-8">
              <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory items-center">
                {[
                  'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/w7jzv59y_Photo%2022-08-2025%2C%2010%2000%2039.jpeg',
                  'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/qmp7ibb4_Photo%2022-08-2025%2C%2010%2002%2046.jpeg',
                  'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/hd6tpeex_Photo%2022-08-2025%2C%2010%2009%2023.jpeg',
                  'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/fhbdlxsu_Photo%2022-08-2025%2C%2014%2038%2034.jpeg',
                  'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/48aqdlbg_Photo%2022-08-2025%2C%2014%2035%2047.jpeg',
                  'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/p2yowlqk_Photo%2022-08-2025%2C%2009%2022%2050.jpeg',
                  'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/irqbg3lu_Photo%2022-08-2025%2C%2014%2043%2014.jpeg',
                ].map((src, index) => (
                  <img
                    key={index}
                    src={src}
                    alt={`Training in action ${index + 1}`}
                    className="flex-shrink-0 h-96 w-auto rounded-xl shadow-lg snap-center"
                  />
                ))}
              </div>
              <p className="text-center text-sage-500 text-sm mt-2">← Scroll to see more →</p>
            </div>
          </div>
        </div>
      </section>

      {/* Course Features */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-8 text-center">
              What's Included in Your Course
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { feature: courseDetails.features[0], icon: BookOpen },
                { feature: courseDetails.features[1], icon: Scissors },
                { feature: courseDetails.features[2], icon: Users },
                { feature: courseDetails.features[3], icon: Gift },
                { feature: courseDetails.features[5], icon: ClipboardCheck },
                { feature: courseDetails.features[6], icon: MessageCircle },
                { feature: "Professional training manual", icon: BookOpen },
                { feature: "Business advice & ongoing support", icon: Phone }
              ].map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <Card key={index} className="border-sage-200 bg-gradient-to-br from-white to-sage-50 hover:shadow-lg transition-all hover:-translate-y-1">
                    <CardContent className="p-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-sage-500 to-sage-700 rounded-lg flex items-center justify-center mb-2">
                        <IconComponent className="text-white" size={20} />
                      </div>
                      <h3 className="text-sm font-bold text-sage-900 mb-1 leading-tight">{item.feature.split(' - ')[0]}</h3>
                      {item.feature.includes(' - ') && (
                        <p className="text-xs text-sage-600 leading-snug">{item.feature.split(' - ')[1]}</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Training Materials */}
      <section className="py-12 bg-gradient-to-br from-sage-50 to-sage-100">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <img
              src="https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/stkyizac_e470c5ef-7048-4657-9fcc-12d68731f3e1.jpeg"
              alt="Professional dog grooming training manual"
              className="w-full max-w-md mx-auto rounded-2xl shadow-xl mb-6"
            />
            <p className="text-sage-700 italic">
              Your own professional training manual with step-by-step guides and helpful tips
            </p>
          </div>
        </div>
      </section>

      {/* Artero Product Gallery */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Section Title */}
            <div className="text-center mb-10">
              <div className="inline-block bg-sage-100 text-sage-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                Worth {courseDetails.starterKitValue}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-3">
                Professional Starter Kit Included
              </h2>
              <p className="text-lg text-sage-600">
                Everything you need to start grooming professionally from day one
              </p>
            </div>

            {/* Artero Logo */}
            <div className="flex justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/butbn1bv_artero.jpg"
                alt="Artero - Since 1909 - Inspiring Style"
                className="h-28 md:h-36 object-contain"
              />
            </div>
            
            {/* Product Images */}
            <div className="grid md:grid-cols-2 gap-8 items-center mt-5 md:mt-0">
              <div className="flex justify-center">
                {/* Desktop Image */}
                <img
                  src="https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/g3lsso1g_AE08C548-0B42-4CFF-9033-7681FF89FA24%202.jpg"
                  alt="Artero School Kit - Professional grooming tools including clippers, brushes, scissors and spray"
                  className="hidden md:block max-w-full h-auto rounded-lg"
                />
                {/* Mobile Image */}
                <img
                  src="https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/iybinuyf_AE08C548-0B42-4CFF-9033-7681FF89FA24.jpeg"
                  alt="Artero School Kit - Professional grooming tools including clippers, brushes, scissors and spray"
                  className="block md:hidden max-w-full h-auto rounded-lg aspect-[5/4] object-contain"
                />
              </div>
              <div className="flex justify-center">
                {/* Desktop Image */}
                <img
                  src="https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/tx5nqb32_49A349B3-B2D1-4814-96EF-8BB21B8C125E%203.jpg"
                  alt="Artero School Kit - Professional clipper blades, scissors and comb attachments"
                  className="hidden md:block max-w-full h-auto rounded-lg"
                />
                {/* Mobile Image */}
                <img
                  src="https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/c9dhs1oc_946A845C-0982-4321-B309-7DAB4F918638.jpeg"
                  alt="Artero School Kit - Professional clipper blades, scissors and comb attachments"
                  className="block md:hidden max-w-full h-auto rounded-lg aspect-[5/4] object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Starter Kit Breakdown */}
      <section className="py-20 bg-sage-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
              What's Included in Your Kit?
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {[
                { name: 'Corded Clippers', desc: 'Professional-grade corded clippers for precise, consistent cutting across all coat types' },
                { name: 'Comb', desc: 'Stainless steel grooming comb for detangling, finishing and checking coat texture' },
                { name: 'Slicker Brush (Large & Small)', desc: 'Two sizes of slicker brush for removing knots, mats and loose hair on any breed' },
                { name: 'Small Straight Scissors', desc: 'Precision straight scissors for detailed finishing work around the face and paws' },
                { name: 'Large Straight Scissors', desc: 'Full-size professional straight scissors for body lines and overall shaping' },
                { name: 'Curved Scissors', desc: 'Curved blade scissors for following body contours and achieving smooth, rounded finishes' },
                { name: 'Blending Scissors', desc: 'Thinning/blending scissors to seamlessly blend coat lengths for a natural look' },
                { name: 'Nail Trimmers', desc: 'Professional nail clippers designed for a clean, safe cut on dogs of all sizes' },
                { name: 'Rubber Mitt', desc: 'Deshedding rubber mitt for removing loose fur and giving a gentle massage finish' },
                { name: 'Blade & Scissor Oil Spray', desc: 'Lubricating spray to keep blades and scissors clean, sharp and running smoothly' },
                { name: 'Box of Comb Attachments', desc: 'A full set of clip-on comb guides for achieving consistent coat lengths' },
                { name: 'Various Clipper Blades', desc: 'A selection of professional blades in different sizes for versatile styling on all breeds' },
              ].map((item, index) =>
              <div key={index} className="flex items-start gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-lg">
                  <Check className="text-sage-500 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-semibold">{item.name}</p>
                    <p className="text-sm text-sage-200 mt-0.5">{item.desc}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Our Academy */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-4">
              Why Train With Us?
            </h2>
            <p className="text-lg text-sage-600 max-w-2xl mx-auto">
              Whether you want to work in a salon or start your own business, we'll help you achieve your dream
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <Card className="border-sage-200 hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-sage-100 to-sage-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Users className="text-sage-700" size={32} />
                </div>
                <h3 className="text-xl font-bold text-sage-900 mb-3">Small Classes</h3>
                <p className="text-sage-600">
                  Maximum 4 students per course ensuring personalised attention and hands-on practice
                </p>
              </CardContent>
            </Card>

            <Card className="border-sage-200 hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-sage-100 to-sage-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <BookOpen className="text-sage-700" size={32} />
                </div>
                <h3 className="text-xl font-bold text-sage-900 mb-3">Real Experience</h3>
                <p className="text-sage-600">
                  Hands-on practice with real dogs in our working salon, not just demonstrations
                </p>
              </CardContent>
            </Card>

            <Card className="border-sage-200 hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-sage-100 to-sage-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Award className="text-sage-700" size={32} />
                </div>
                <h3 className="text-xl font-bold text-sage-900 mb-3">Expert Tutors</h3>
                <p className="text-sage-600">
                  Friendly, professional groomers who teach from basics to advanced techniques
                </p>
              </CardContent>
            </Card>

            <Card className="border-sage-200 hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-sage-100 to-sage-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Briefcase className="text-sage-700" size={32} />
                </div>
                <h3 className="text-xl font-bold text-sage-900 mb-3">Business Support</h3>
                <p className="text-sage-600">
                  Learn how to start and run your own successful grooming business from day one
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 max-w-3xl mx-auto">
            <Card className="border-sage-200 bg-gradient-to-br from-sage-50 to-sage-50">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold text-sage-900 mb-4">
                  Perfect for Beginners & Upskillers
                </h3>
                <p className="text-lg text-sage-700">
                  Whether you're starting from scratch or looking to enhance your skills, 
                  our course provides everything you need to succeed in the dog grooming industry.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Student Reviews */}
      <section className="py-20 bg-gradient-to-br from-sage-50 to-sage-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-3">Student Reviews</h2>
            <div className="flex justify-center gap-1 mb-2">
              {[...Array(5)].map((_, i) => <Star key={i} className="fill-amber-400 text-amber-400" size={24} />)}
            </div>
            <p className="text-sage-600">Hear from our graduates</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              {
                name: "Spruced Pup",
                initials: "SP",
                text: "Recently completed the new dog grooming course offered at Pups & Dreams. What an amazing week we had. Emily and Megan were both friendly and extremely informative, guiding us through each step of the dog grooming process. We laughed all week, loved the many dogs we had the pleasure of grooming and would highly recommend this course to any aspiring dog groomer. We've now set up our very own grooming salon 'Spruced Pup' in Thornham, Royton and will be opening very soon! Many thanks to Emily and Megan for an unforgettable week. Could not have done this without you! Xx"
              },
              {
                name: "Amy Stewart",
                initials: "AS",
                text: "Just completed a week's course on an introductory into dog grooming and what a week it's been. I've thoroughly enjoyed every minute. I can't recommend Megan and Emily enough — their passion and knowledge is second to none, the grooming salon is absolutely beautiful. They are so patient with the students. You have the chance to get to grips with actual models (dogs), it's all hands on training rather than classroom based and assignments. Can't thank you both enough xxx"
              },
              {
                name: "Tanya Parker",
                initials: "TP",
                text: "Joined Megan and Emily's grooming training course and it was absolutely amazing — so friendly and easy to approach. They were there to answer any questions and support throughout all of it. Absolutely fantastic week! Would really recommend if anyone is wanting any hands-on experience."
              },
              {
                name: "Alex Rooney",
                initials: "AR",
                text: "I did a grooming course at this beautiful establishment. I wouldn't have chosen anywhere different — thank you to both of the girls, Megan and Emily, you're both so amazing!! If you're going to do a dog grooming course, this is the place to do it!!! I learnt everything I needed to know here!!! Thank you girls."
              },
              {
                name: "Grace",
                initials: "G",
                text: "I recently completed a dog grooming course with the girls at Pups and Dreams and to say I loved my week would be an understatement. I've been toying with this career move for years and finally decided to take the plunge. I couldn't be happier with where I chose to do my training. I wanted a hands-on course with minimal coursework — I have ADHD and find heavy theory hard to resonate with, and much prefer learning on the job. This course was perfect for that. We covered everything you could imagine, had so much support in a beautiful environment, and by the end of the week we had groomed 9 dogs. I left feeling so excited for my future in this business. Thank you Emily and Megan — anyone looking for a dog grooming course, get in touch with Pups and Dreams. You won't regret it."
              }
            ].map((review, index) => (
              <Card key={index} className={`border-sage-200 bg-white shadow-sm hover:shadow-md transition-shadow ${index === 4 ? 'md:col-span-2 lg:col-span-1' : ''}`}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-sage-500 to-sage-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-bold">{review.initials}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-sage-900">{review.name}</p>
                      <div className="flex gap-0.5">
                        {[...Array(5)].map((_, i) => <Star key={i} className="fill-amber-400 text-amber-400" size={14} />)}
                      </div>
                    </div>
                  </div>
                  <p className="text-sage-700 text-sm leading-relaxed">"{review.text}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-3">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-sage-600">
                Everything you need to know about our CPD dog grooming course
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-3">
              {[
                {
                  q: "Do I need prior experience to attend the CPD course?",
                  a: "The course is perfect for beginners who want a hands-on introduction to dog grooming. No prior dog grooming experience is required. Our CPD courses are open to all learners aged 18 and over who have a love for dogs and a willingness to learn."
                },
                {
                  q: "What is the course price and what is included?",
                  a: (
                    <div className="space-y-3">
                      <p>The course total price is <strong>£2,000</strong>.</p>
                      <p>The course fee includes:</p>
                      <ul className="space-y-1.5 ml-4">
                        {[
                          "5 full days of professional, in-salon training (Monday–Friday 10am–4pm)",
                          "A premium Artero grooming kit worth £800 (yours to keep!) — given to you on Day 1",
                          "Use of additional salon tools and equipment (bath, hair dryers, tables, towels and real dogs to work with)",
                          "Your own training manual with helpful tips & step-by-step guides",
                          "'Introduction to Dog Grooming' CPD accredited in-house certificate",
                          "Light refreshments (tea/coffee)"
                        ].map((item, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <Check className="text-sage-600 flex-shrink-0 mt-0.5" size={16} />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                },
                {
                  q: "When are the courses and how often do they run?",
                  a: "Please send us an email or arrange a call to find out when our next available course is. Course dates run every few months throughout the year. As we're both dog groomers for our full-time job too, our courses need to run on 5 consecutive days — so unfortunately cannot be completed 1 day per week etc."
                },
                {
                  q: "What is the deposit and how do I pay?",
                  a: "The deposit is £1,000 and this needs to be paid within 24 hours of receiving your booking form via bank transfer. The deposit is needed to order your kit. Once the booking form is signed you will receive confirmation that your place has been secured. The remaining balance needs to be paid 2 weeks before the course start date. We will send reminder emails for final payment. Payments can be made via bank transfer to our business account (details given once you confirm you'd like to attend and pay the deposit)."
                },
                {
                  q: "Do you offer payment plans?",
                  a: "Unfortunately we do not currently offer payment plans."
                },
                {
                  q: "Do I need to bring my own equipment?",
                  a: "No — you'll receive a brand new Artero grooming kit on Day 1, which we will go through with you and is yours to keep. You'll also have access to our full range of salon tools throughout your training (bath, hairdryers, towels and tables). You'll receive your own training manual and grooming diaries to make notes, but we'd also recommend bringing an additional notepad and a bag to take home your equipment on the last day."
                },
                {
                  q: "Will I receive a certificate and is the course accredited?",
                  a: "Yes — after completing the 5-day course, you'll receive an in-house Pups & Dreams CPD Certificate of Completion. Our courses/certificates are CPD accredited. Please note: our course is not regulated by Ofqual (government regulated qualification). However, you do not need an Ofqual qualification to work as a dog groomer as the industry remains unregulated."
                },
                {
                  q: "What should I wear?",
                  a: "Wear something comfy that you don't mind getting hairy — you'll be on your feet and getting hands-on! Closed-toe, non-slip shoes are a must for safety."
                },
                {
                  q: "Is lunch provided?",
                  a: "We don't provide lunch, but we do keep the tea and coffee flowing! We recommend bringing your own flask (to keep the dog hair out!) Bring a packed lunch or grab something locally — there are plenty of food spots nearby."
                },
                {
                  q: "Can I take photos or videos during the course?",
                  a: "Absolutely! Take as many photos of your own work as you like. We'll also take photos for social media — but let us know if you'd prefer not to be featured."
                },
                {
                  q: "How many students are on each course?",
                  a: "We keep our class sizes small with only 4 students to ensure you get plenty of one-to-one support and hands-on experience."
                },
                {
                  q: "Will I be grooming real dogs?",
                  a: "Yes — every day of your course includes hands-on practice with real dogs under expert supervision. You'll groom 9 dogs across a variety of breeds (long & short hair, small & large size), including common breeds such as Shih-tzus, Spaniels, French Bulldogs, Cockapoos and more!"
                },
                {
                  q: "Can I park at the academy?",
                  a: "We don't have a personal car park but there is free car parking on side streets near the salon, as well as a car park opposite."
                },
                {
                  q: "What if I need to cancel or reschedule?",
                  a: "The £1,000 deposit to secure your spot is non-refundable as this pays for your Artero equipment kit which is ordered from Spain and secures your space on the course. If you cancel with less than 14 days' notice, the remaining course balance is also non-refundable, as your space, training manual, model dogs and Artero kit will have already been organised. However, we can try our best to reschedule you to a course later in the year/early next year — but places are limited so we can't guarantee. If your course date needs to be moved, you will receive your kit on Day 1 of the new course date."
                },
                {
                  q: "What if I have allergies or health concerns?",
                  a: "Please let us know in advance if you have any allergies (e.g. grooming products, food) or any medical conditions we should be aware of, to help support you during your training."
                },
                {
                  q: "Will I be assessed during the course?",
                  a: "There's no formal exam, but we will provide ongoing feedback and support throughout the course. You'll leave with a stronger understanding of practical dog grooming and more confidence in your skills."
                },
                {
                  q: "Do you offer any support after the course ends?",
                  a: "Yes! You'll have access to ongoing support through our private student WhatsApp group, and we're always happy to answer questions or help with your grooming journey."
                }
              ].map((faq, index) => (
                <AccordionItem key={index} value={`faq-${index}`} className="border border-sage-200 rounded-xl px-6 bg-white shadow-sm">
                  <AccordionTrigger className="text-left font-semibold text-sage-900 hover:text-sage-600 hover:no-underline py-5">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sage-700 leading-relaxed pb-5">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-sage-50 to-sage-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-4">
              Ready to Start Your Grooming Career?
            </h2>
            <p className="text-xl text-sage-700 mb-8 max-w-2xl mx-auto">
              Enquire today for course dates and pricing. Limited spaces available!
            </p>
            <Link to="/contact">
              <Button size="lg" className="inline-flex items-center justify-center whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-primary/90 h-10 bg-gradient-to-r from-sage-600 to-sage-700 hover:from-sage-700 hover:to-sage-800 text-sm font-medium gap-2 px-8 rounded-md shadow-lg text-white">
                Enquire About Our Next Course <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>);


};

export default AcademyPage;